import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  BadgeCheck,
  Check,
  PhoneCall,
  ShieldCheck,
  Video,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { FadeUp, MaskedLine, ParallaxImage, EASE } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import { useLang } from "@/lib/i18n";
import { useSeo } from "@/lib/useSeo";
import { IMAGES } from "@/constants/site";

const CONTENT = {
  en: {
    eyebrow: "Individual psychotherapy · online sessions",
    h1a: "Meeting you exactly",
    h1b: "where you are.",
    sub: "There is no right way to feel, and no script to follow here. This is a space dedicated to your unique experience — a place to pause, speak freely, and navigate your way forward through genuine dialogue.",
    cta: "Book a Consultation",
    secondary: "How I work",
    trust: [
      "Confidential by design",
      "Registered with BACP — British Association for Counselling & Psychotherapy",
      "Online sessions",
    ],
    freeCallTitle: "Free 15-minute call",
    freeCallBody: "A short introductory call to see if we're a good fit. No commitment.",
    approachEyebrow: "My approach",
    approachH2: "Therapy shaped around you, not a script.",
    cardATitle: "The relationship comes first",
    cardABody:
      "Warmly relational and gently post-existential: rather than treating you as a problem to solve, we take your experience seriously — how you live, what you value, where you feel stuck — and let understanding become the ground for change.",
    quote: "\u201CWe are not problems to be solved, but lives to be understood.\u201D",
    inRoomTitle: "In the room",
    inRoom: [
      "Sessions that move at your pace — never a formula",
      "A registered, supervised practitioner",
      "Honest conversation about what actually helps",
    ],
    values: "Unhurried. Confidential. Yours.",
    moreAbout: "More about me",
    servicesEyebrow: "Services",
    servicesH2: "A calm, structured way to begin.",
    rows: [
      ["01", "Individual therapy", "Weekly 50-minute sessions online, for as long as they're useful."],
      ["02", "Online & secure", "The same unhurried hour over secure video, from wherever you are."],
      ["03", "Areas of focus", "Anxiety, low mood, relationships, burnout, grief and life transitions — among others."],
    ],
    ctaH2: "Ready when you are.",
    ctaSub: "Start with a free 15-minute introductory call — a chance to ask anything, and to see how it feels to talk.",
    ctaBtn: "Book a Consultation",
    videoEyebrow: "A first meeting",
    videoTitle: "Welcome — in a few words.",
  },
  el: {
    eyebrow: "Ατομική ψυχοθεραπεία · συνεδρίες online",
    h1a: "Συναντώντας σε ακριβώς",
    h1b: "εκεί που βρίσκεσαι.",
    sub: "Δεν υπάρχει «σωστός» τρόπος να νιώθεις, ούτε κάποιο σενάριο που πρέπει να ακολουθήσεις. Εδώ είναι ένας χώρος αφιερωμένος στη δική σου, μοναδική εμπειρία — ένα μέρος για να κάνεις μια παύση, να μιλήσεις ελεύθερα και να βρεις τον δρόμο σου μέσα από τον αληθινό διάλογο.",
    cta: "Κλείστε Ραντεβού",
    secondary: "Πώς δουλεύω",
    trust: [
      "Απόλυτα εμπιστευτικό",
      "Εγγεγραμμένος στο BACP — British Association for Counselling & Psychotherapy",
      "Συνεδρίες online",
    ],
    freeCallTitle: "Δωρεάν γνωριμία 15 λεπτών",
    freeCallBody: "Μια σύντομη πρώτη κουβέντα για να δείτε αν ταιριάζουμε. Χωρίς δέσμευση.",
    approachEyebrow: "Η προσέγγισή μου",
    approachH2: "Θεραπεία γύρω από εσάς — όχι από ένα σενάριο.",
    cardATitle: "Πρώτα η σχέση",
    cardABody:
      "Ζεστά σχεσιακή και ήπια μετα-υπαρξιακή προσέγγιση: δεν σας αντιμετωπίζω ως πρόβλημα προς επίλυση, αλλά παίρνω σοβαρά την εμπειρία σας — πώς ζείτε, τι σας νοιάζει, πού νιώθετε κολλημένοι — και αφήνω την κατανόηση να γίνει το έδαφος για αλλαγή.",
    quote: "«Δεν είμαστε προβλήματα που λύνονται, αλλά ζωές που πρέπει να κατανοηθούν.»",
    inRoomTitle: "Στη συνεδρία",
    inRoom: [
      "Συνεδρίες στον ρυθμό σας — ποτέ με βάση κάποιον τύπο",
      "Εγγεγραμμένος θεραπευτής, με συνεχή εποπτεία",
      "Ειλικρινής κουβέντα για ό,τι βοηθάει πραγματικά",
    ],
    values: "Χωρίς βιασύνη. Εμπιστευτικά. Δικός σας χώρος.",
    moreAbout: "Περισσότερα για εμένα",
    servicesEyebrow: "Υπηρεσίες",
    servicesH2: "Ένας ήρεμος, σαφής τρόπος για να ξεκινήσετε.",
    rows: [
      ["01", "Ατομική ψυχοθεραπεία", "Εβδομαδιαίες συνεδρίες 50 λεπτών online, για όσο διάστημα σας είναι χρήσιμες."],
      ["02", "Online & με ασφάλεια", "Η ίδια αβίαστη ώρα μέσω ασφαλούς βιντεοκλήσης, από όπου κι αν βρίσκεστε."],
      ["03", "Θέματα που δουλεύουμε", "Άγχος, καταθλιπτική διάθεση, σχέσεις, burnout, πένθος και μεταβάσεις ζωής — μεταξύ άλλων."],
    ],
    ctaH2: "Όποτε είστε έτοιμοι.",
    ctaSub: "Ξεκινήστε με μια δωρεάν γνωριμία 15 λεπτών — μια ευκαιρία να ρωτήσετε ό,τι θέλετε και να δείτε πώς είναι να μιλάμε.",
    ctaBtn: "Κλείστε Ραντεβού",
    videoEyebrow: "Μια πρώτη γνωριμία",
    videoTitle: "Καλώς ορίσατε — με λίγα λόγια.",
  },
};

const Home = () => {
  const { lang } = useLang();
  const c = CONTENT[lang];
  useSeo("home");

  return (
    <div data-testid="page-home">
      {/* Hero */}
      <section className="container-x pb-20 pt-32 md:pt-40 lg:pb-28 lg:pt-44">
        <div className="grid items-center gap-14 lg:grid-cols-12 lg:gap-20">
          <div className="lg:col-span-7">
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1, ease: EASE }}
              className="eyebrow mb-8"
            >
              {c.eyebrow}
            </motion.p>
            <h1 className="font-display text-4xl font-light leading-[1.05] tracking-tight text-sage-900 sm:text-6xl lg:text-7xl">
              <MaskedLine delay={0.2}>{c.h1a}</MaskedLine>
              <MaskedLine delay={0.38}>
                <em className="italic text-sage-600">{c.h1b}</em>
              </MaskedLine>
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.75, ease: EASE }}
              className="mt-8 max-w-xl text-lg leading-relaxed text-muted-foreground"
            >
              {c.sub}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.9, ease: EASE }}
              className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-4"
            >
              <Button
                asChild
                data-testid="hero-cta-book"
                className="rounded-full bg-sage-600 px-8 py-6 text-base text-background transition-colors duration-300 hover:bg-sage-700"
              >
                <Link to="/intake" className="inline-flex items-center gap-2">
                  {c.cta} <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
              <Link
                to="/services"
                data-testid="hero-link-how-i-work"
                className="link-underline text-sm font-medium text-sage-700"
              >
                {c.secondary}
              </Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 1.1 }}
              className="mt-14 flex flex-wrap gap-x-10 gap-y-4 border-t border-sage-600/10 pt-8"
            >
              {c.trust.map((label, i) => {
                if (label.includes("BACP")) {
                  return (
                    <div key={label} data-testid="home-bacp-badge" className="flex items-center">
                      <img
                        src="/bacp-logo.webp"
                        alt="BACP Registered Member 420016 — MBACP"
                        className="h-14 w-auto rounded-xl bg-white p-2 ring-1 ring-sage-600/10"
                        loading="lazy"
                      />
                    </div>
                  );
                }
                const icons = [ShieldCheck, BadgeCheck, Video];
                const Icon = icons[i] || ShieldCheck;
                return (
                  <p key={label} className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Icon className="h-4 w-4 shrink-0 text-sage-500" strokeWidth={1.75} />
                    {label}
                  </p>
                );
              })}
            </motion.div>
          </div>

          <div className="lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, scale: 0.97, y: 24 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 1.2, delay: 0.5, ease: EASE }}
              className="relative mx-auto max-w-md lg:max-w-none"
            >
              <ParallaxImage
                src="/svanen.jpg"
                alt="Hilma af Klint, «Ο Κύκνος» (Svanen), 1915 — αφηρημένος πίνακας με απαλά χρωματικά πεδία"
                className="aspect-[4/5] rounded-b-3xl rounded-t-[999px]"
                distance={40}
              />
              <div className="grain-card absolute -bottom-6 -left-2 max-w-[250px] bg-card p-5 sm:-left-6">
                <p className="flex items-center gap-2 text-sm font-semibold text-sage-800">
                  <PhoneCall className="h-4 w-4 text-clay" strokeWidth={1.75} />
                  {c.freeCallTitle}
                </p>
                <p className="mt-2 text-xs leading-relaxed text-muted-foreground">{c.freeCallBody}</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <Marquee />

      {/* Welcome video — Greek version only */}
      {/* Approach bento */}
      <section className="container-x py-24 lg:py-32">
        <FadeUp>
          <p className="eyebrow mb-6">{c.approachEyebrow}</p>
          <h2 className="max-w-2xl font-display text-4xl font-light leading-tight tracking-tight text-sage-900 sm:text-5xl">
            {c.approachH2}
          </h2>
        </FadeUp>
        <div className="mt-14 grid gap-6 md:grid-cols-3">
          <FadeUp className="md:col-span-2">
            <div className="grain-card flex h-full flex-col justify-between bg-sage-600 p-10 text-background lg:p-12">
              <h3 className="font-display text-2xl font-light lg:text-3xl">{c.cardATitle}</h3>
              <p className="mt-8 max-w-lg leading-relaxed text-background/85">{c.cardABody}</p>
            </div>
          </FadeUp>
          <FadeUp delay={0.1}>
            <ParallaxImage
              src={IMAGES.abstract}
              alt="Soft abstract green texture"
              className="h-full min-h-[280px] rounded-3xl"
              distance={30}
            />
          </FadeUp>
          <FadeUp>
            <div className="grain-card flex h-full flex-col justify-center bg-secondary p-10">
              <p className="font-display text-2xl italic leading-snug text-sage-800">{c.quote}</p>
            </div>
          </FadeUp>
          <FadeUp delay={0.05}>
            <div className="grain-card h-full bg-card p-10">
              <h3 className="text-sm font-semibold uppercase tracking-[0.18em] text-sage-600">
                {c.inRoomTitle}
              </h3>
              <ul className="mt-6 space-y-4">
                {c.inRoom.map((item) => (
                  <li key={item} className="flex items-start gap-3 text-sm leading-relaxed text-foreground/85">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-clay" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </FadeUp>
          <FadeUp delay={0.1}>
            <div className="grain-card flex h-full flex-col justify-between bg-card p-10">
              <p className="font-display text-3xl font-light leading-snug text-sage-800">{c.values}</p>
              <Link
                to="/about"
                data-testid="home-approach-about-link"
                className="link-underline mt-8 inline-flex items-center gap-2 text-sm font-medium text-sage-700"
              >
                {c.moreAbout} <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* Services teaser */}
      <section className="border-t border-sage-600/10 bg-secondary/40">
        <div className="container-x py-24 lg:py-32">
          <FadeUp>
            <p className="eyebrow mb-6">{c.servicesEyebrow}</p>
            <h2 className="max-w-2xl font-display text-4xl font-light leading-tight tracking-tight text-sage-900 sm:text-5xl">
              {c.servicesH2}
            </h2>
          </FadeUp>
          <div className="mt-14">
            {c.rows.map(([num, title, line], i) => (
              <FadeUp key={num} delay={i * 0.06}>
                <Link
                  to="/services"
                  data-testid={`home-service-row-${num}`}
                  className="group flex items-center justify-between gap-6 border-t border-sage-600/10 py-8 transition-colors duration-300 last:border-b hover:bg-background/60 md:py-10"
                >
                  <div className="flex items-baseline gap-6 md:gap-10">
                    <span className="font-display text-sm italic text-clay">{num}</span>
                    <span className="font-display text-2xl font-light text-sage-900 md:text-3xl">{title}</span>
                  </div>
                  <div className="hidden max-w-sm items-center gap-6 md:flex">
                    <span className="text-sm leading-relaxed text-muted-foreground">{line}</span>
                    <ArrowRight className="h-5 w-5 shrink-0 text-sage-600 transition-transform duration-300 group-hover:translate-x-1" />
                  </div>
                  <ArrowRight className="h-5 w-5 shrink-0 text-sage-600 md:hidden" />
                </Link>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="container-x py-24 lg:py-32">
        <FadeUp>
          <div className="grain-card bg-sage-600 px-8 py-16 text-center text-background md:px-16 lg:py-24">
            <h2 className="mx-auto max-w-2xl font-display text-4xl font-light leading-tight tracking-tight sm:text-5xl">
              {c.ctaH2}
            </h2>
            <p className="mx-auto mt-6 max-w-xl leading-relaxed text-background/85">{c.ctaSub}</p>
            <Button
              asChild
              data-testid="home-cta-book"
              className="mt-10 rounded-full bg-background px-8 py-6 text-base text-sage-700 transition-colors duration-300 hover:bg-clay-light"
            >
              <Link to="/intake">{c.ctaBtn}</Link>
            </Button>
          </div>
        </FadeUp>
      </section>
    </div>
  );
};

export default Home;
